// Package imports:
import React, { useMemo } from "react";
import { Formik } from "formik";
import Bugsnag from "@bugsnag/js";
// Component imports:
import Modal from "../../../ui-elements/Modal/Modal";
import { EditContactForm, validate } from "./EditContactForm";
// Service imports:
import { ErrorMessages } from "../../../services/errorMessages";
// Type imports:
import { ContactsOverview, EditContactFormValues, IDueDiligenceModalProps, KnowYourCustomer, RiskScoreDto, ServiceCompanyEmployee } from "../../../types/DueDiligenceTypes";
import { Fetched } from "../../../types/Types";

interface IProps extends IDueDiligenceModalProps {
    data: { customerId: number; companyName: string; confirmProcurations: boolean; supervisorWebUserId?: number | null; contactEmail?: string | null };
    contacts: ContactsOverview | null;
    customers?: Fetched<KnowYourCustomer[]>;
    setCustomers?: React.Dispatch<React.SetStateAction<Fetched<KnowYourCustomer[]>>>;
    sendNotification?: boolean;
    riskScore?: RiskScoreDto;
    setRiskScore?: React.Dispatch<React.SetStateAction<RiskScoreDto>>;
    employees: ServiceCompanyEmployee[];
}

const EditContactModal: React.FC<IProps> = ({
    show,
    close,
    setToastShow,
    setToastType,
    setToastMessage,
    data,
    contacts,
    customers,
    setCustomers,
    sendNotification,
    riskScore,
    setRiskScore,
    employees
}) => {
    // Track form-level errors from backend responses
    const [formError, setFormError] = React.useState<string | undefined>(undefined);
    // Disable submit button during API calls to prevent double submission
    const [disableSubmit, setDisableSubmit] = React.useState(false);

    // Reset form state when modal opens
    React.useEffect(() => {
        if (show) {
            setFormError(undefined);
            setDisableSubmit(false);
        }
    }, [show]);
    /**
     * Main form submission handler - called ONLY when form validation passes
     * 
     * Flow:
     * 1. Update procuration confirmation setting (confirm_procurations field in database)
     * 2. Update contact information (contact details)
     * 3. Optionally send notification email to the contact
     * 4. Update local state to reflect changes in the UI
     * 5. Close the modal and show success message
     */
    const handleSubmit = async (values: EditContactFormValues) => {
        try {
            // Disable the submit button to prevent multiple submissions
            setDisableSubmit(true);

            // STEP 1: Update the procuration confirmation setting
            // This updates the confirm_procurations field in the database
            // We call this every time to ensure the database always reflects the current checkbox state
            const procurationResponse = await fetch(`/DueDiligence/SetConfirmProcuration/${values.customerId}?doIt=${values.confirmProcuration}`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' }
            });

            if (!procurationResponse.ok) {
                const errorData = await procurationResponse.text();
                setToastType("error");
                setToastMessage(errorData || 'Ekki tókst að breyta stillingum');
                setToastShow(true);
                return;
            }

            // STEP 2: Update the contact information
            // Prepare payload - convert empty SSN to null for backend
            const payload = {
                ...values,
                ssn: (values.ssn === "" || values.alien) ? null : values.ssn,
                dateOfBirth: values.dateOfBirth ? new Date(values.dateOfBirth).toISOString() : null,
                nationality: values.nationality ?? null,
                supervisorId: values.supervisorId ?? null
            };

            const contactResponse = await fetch('/DueDiligence/UpdateCompanyContact', {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });

            if (!contactResponse.ok) {
                const errorData = await contactResponse.text();
                setFormError(errorData || 'Ekki tókst að skrá tengilið');
                return;
            }

            // STEP 3: Optionally send notification email to the contact
            // This sends a due diligence questionnaire link to the contact
            if (sendNotification) {
                const notifyResponse = await fetch(`/DueDiligence/SendNotification?customerId=${data.customerId}`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' }
                });

                if (!notifyResponse.ok) {
                    const errorData = await notifyResponse.text();
                    setFormError(errorData || 'Ekki tókst að senda Áreiðanleikakönnun');
                    return;
                }

                setToastMessage("Áreiðanleikakönnun send");
            } else {
                setToastMessage("Tengiliði breytt");
            }

            // STEP 4: Update local state to reflect changes
            setToastType("success");
            setToastShow(true);
            setFormError(undefined);

            // Update the risk score's dateSent if we sent a notification
            if (riskScore && setRiskScore) {
                setRiskScore({ ...riskScore, dateSent: new Date().toISOString() });
            }

            // Update the customers list in the parent component to reflect both changes:
            // - The contact information update
            // - The procuration confirmation setting
            // - also supervisorId change so that on next open the correct supervisor is shown
            if (setCustomers) {
                updateCustomers(values, values.confirmProcuration);
            }

            // STEP 5: Close the modal - all operations completed successfully
            close();
        } catch (err) {
            // Log unexpected errors to Bugsnag for monitoring
            if (err instanceof Error) Bugsnag.notify(err);
            setToastType("error");
            setToastMessage(ErrorMessages.OtherShort);
            setToastShow(true);
        } finally {
            // Always re-enable the submit button, even if there was an error
            setDisableSubmit(false);
        }
    }

    /**
     * Updates the local customers state to reflect changes made in the form
     * This prevents needing to refetch all customer data after an update
     * Uses functional setState to avoid stale closure issues
     * 
     * @param values - The form values containing updated contact information
     * @param hasProcuration - The procuration confirmation setting to update
     */
    const updateCustomers = (values: EditContactFormValues, hasProcuration: boolean) => {
        if (!setCustomers) return;

        // Use functional update to get the latest state
        setCustomers((prevCustomers) => {
            // Check if customers data is valid
            if (!prevCustomers || prevCustomers instanceof Error) return prevCustomers;

            // Map through customers and update the matching one
            const updatedCustomers = prevCustomers.map(cust => {
                // Only update the customer that matches the customerId
                if (cust.customerId === values.customerId) {
                    return {
                        ...cust,
                        // Update all contact information fields
                        contactSSN: values.ssn ?? '',
                        contactName: values.name,
                        contactAddress: values.address,
                        contactAreaCode: values.areaCode,
                        contactCountry: values.country,
                        contactNationality: values.nationality ?? '',
                        contactEmail: values.email,
                        contactRegion: values.region,
                        supervisorId: values.supervisorId ?? null,
                        // Update the procuration confirmation setting
                        confirmProcurations: hasProcuration
                    };
                }
                // Return unchanged customer if ID doesn't match
                return cust;
            });
            return updatedCustomers;
        });
    }

    /**
     * Initialize form values based on current contact data
     * Memoized to prevent recreation on every render
     */
    const initialValues: EditContactFormValues = useMemo(() => {
        return contacts?.currentContact
            ? {
                // Use existing contact data if available
                alien: contacts.currentContact.alien,
                customerId: data.customerId,
                ssn: contacts.currentContact.contactSSN ?? '',
                name: contacts.currentContact.contactName ?? '',
                address: contacts.currentContact.contactAddress ?? '',
                areaCode: contacts.currentContact.contactAreaCode ?? '',
                country: contacts.currentContact.contactCountry ?? '',
                nationality: contacts.currentContact.contactNationality ?? '',
                region: contacts.currentContact.contactRegion ?? '',
                email: contacts.currentContact.contactEmail ?? data.contactEmail ?? '',
                companyName: data.companyName,
                confirmProcuration: contacts?.shouldProcurersConfirm ?? false,
                dateOfBirth: contacts.currentContact.contactDateOfBirth ?? null,
                supervisorId: (contacts.currentContact as any).supervisorId ?? data.supervisorWebUserId ?? null,
                // Filter and map procuration holders from possible contacts
                procurers: contacts?.possibleContacts.filter(c => c.hasProcuration).map(c => ({
                    name: `${c.firstname} ${c.lastname}`,
                    email: c.email ?? '',
                    ssn: c.ssn
                })) || []
            } : {
                // Use empty defaults if no existing contact
                alien: false,
                customerId: data.customerId,
                ssn: '',
                name: '',
                address: '',
                areaCode: '',
                nationality: '',
                country: '',
                region: '',
                email: data.contactEmail ?? '',
                companyName: data.companyName,
                confirmProcuration: false,
                dateOfBirth: null,
                supervisorId: data.supervisorWebUserId ?? null,
                procurers: contacts?.possibleContacts.filter(c => c.hasProcuration).map(c => ({
                    name: `${c.firstname} ${c.lastname}`,
                    email: c.email ?? '',
                    ssn: c.ssn
                })) || []
            };
    }, [contacts, data]);

    /**
     * Renders the modal body content
     * Shows instructions and the Formik form
     */
    const renderBody = () => (
        <div className="KCL_edit-contact-modal">
            {/* Show notification warning if sendNotification is enabled */}
            {sendNotification &&
                <p className="paragraph">
                    Þú ert að fara að senda áreiðanleikakönnun á {data.companyName}. Tengiliður fær tölvupóst með hlekk á spurningalista.
                </p>
            }
            <p className="paragraph">
                Tengiliður skal ávallt vera sá sem sérstaka heimild hefur skv. lögum til að koma fram fyrir hönd viðskiptamanns gagnvart þínu félagi.
            </p>
            {/* 
                Formik wrapper component that handles:
                - Form state management
                - Validation (via validate function)
                - Submission (via handleSubmit)
                
                validateOnMount={false} prevents validation from running immediately on mount
                This improves UX by not showing errors before user interacts with the form
                
                enableReinitialize={false} prevents Formik from resetting form values when
                the component re-renders (e.g., when formError state changes)
                
                Using render prop instead of component prop to prevent form reset when
                disableSubmit or formError state changes during submission
            */}
            <Formik
                initialValues={initialValues}
                validate={validate}
                onSubmit={handleSubmit}
                validateOnMount={false}
                enableReinitialize={false}
            >
                {(formikProps) => (
                    <EditContactForm
                        contacts={contacts}
                        values={formikProps.values}
                        closeModal={close}
                        formError={formError}
                        disableSubmit={disableSubmit}
                        sendNotification={sendNotification}
                        employees={employees}
                    />
                )}
            </Formik>
        </div>
    )

    return (
        <Modal
            show={show}
            onHide={close}
            header={`Skrá tengilið fyrir ${data.companyName}`}
            body={renderBody()}
            hasCloseButton
        />
    )
}

export default EditContactModal;